import {
  Box,
  Typography,
  Button,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import { FieldWrapper } from '../Styles';
import { RecordContainer } from '../Styles';
import { daysOffsetOptions, maxMonthOptions } from './constants';
import { FilterOptionDto } from '@act/shared/data-transfer-objects';
import { themeColors } from '@act/storybook';

export type ManageSegmentOverridesProps = {
  overrides: {
    segmentId: number;
    maxMonth?: number;
    offsetValue: 'avgProgramDays' | number;
  }[];
  handleOverridesUpdated: (values) => void;
  segmentOptions: FilterOptionDto[];
};

export const ManageSegmentOverrides = ({
  overrides,
  handleOverridesUpdated,
  segmentOptions,
}: ManageSegmentOverridesProps) => {
  const overridesList = [...overrides];
  const addNewOverride = () => {
    // Initialize with first available segment and null for optional fields
    const firstUnusedSegment = segmentOptions.find(
      segment => !usedSegmentIds.includes(segment.value as number)
    );
    overridesList.push({
      segmentId:
        (firstUnusedSegment?.value as number) ??
        (segmentOptions[0]?.value as number),
      offsetValue: null,
      maxMonth: null,
    });
    handleOverridesUpdated(overridesList);
  };
  const usedSegmentIds = overrides.map(value => value.segmentId);

  const updateOverride = (value, index) => {
    overridesList[index] = value;
    handleOverridesUpdated([...overridesList]);
  };

  const removeOverride = index => {
    const updatedList = overridesList.filter((_, ind) => ind !== index);
    handleOverridesUpdated([...updatedList]);
  };

  return (
    <RecordContainer>
      <Box
        sx={{
          display: 'flex',
          algnItems: 'center',
          justifyContent: 'space-between',
          alignSelf: 'stretch',
        }}
      >
        <Typography variant="headlineMedium">Segment Overrides</Typography>
        <Button variant="outlined" onClick={addNewOverride}>
          Add Override
        </Button>
      </Box>
      {overrides.map((override, index) => {
        return (
          <RecordContainer key={`override_${index}`}>
            <Typography variant="subheadMedium">
              {segmentOptions.find(
                segment => segment.value === override.segmentId
              )?.label || 'Segment'}
            </Typography>
            <Divider />
            <Box display="flex" flexDirection="column" width="100%">
              <FieldWrapper>
                <FormControl style={{ width: '100%' }}>
                  <InputLabel id="segment-label">Segment</InputLabel>
                  <Select
                    labelId="segment-label"
                    value={override?.segmentId ?? ''}
                    label="Segment"
                    fullWidth
                    onChange={event =>
                      updateOverride(
                        {
                          ...override,
                          segmentId:
                            event.target.value === ''
                              ? null
                              : event.target.value,
                        },
                        index
                      )
                    }
                  >
                    {segmentOptions.map(segment => (
                      <MenuItem
                        key={segment.value}
                        value={segment.value}
                        disabled={usedSegmentIds.includes(
                          segment.value as number
                        )}
                      >
                        {segment.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FieldWrapper>
              <FieldWrapper>
                <FormControl style={{ width: '100%' }}>
                  <InputLabel id="offset-label">
                    Assignment Offset Days
                  </InputLabel>
                  <Select
                    labelId="offset-label"
                    value={override?.offsetValue ?? ''}
                    label="Assignment Offset Days"
                    fullWidth
                    onChange={event =>
                      updateOverride(
                        {
                          ...override,
                          offsetValue:
                            event.target.value === ''
                              ? null
                              : event.target.value,
                        },
                        index
                      )
                    }
                  >
                    {daysOffsetOptions.map(option => (
                      <MenuItem
                        key={option.value ?? 'none'}
                        value={option.value ?? ''}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FieldWrapper>
              <FieldWrapper>
                <FormControl style={{ width: '100%' }}>
                  <InputLabel id="max-month-label">
                    Last Month Available
                  </InputLabel>
                  <Select
                    labelId="max-month-label"
                    label="Last Month Avaiable"
                    value={override?.maxMonth ?? ''}
                    name="Last Month Available"
                    renderValue={value =>
                      maxMonthOptions.find(month => month.value === value)
                        ?.label
                    }
                    fullWidth
                    onChange={event =>
                      updateOverride(
                        {
                          ...override,
                          maxMonth:
                            event.target.value === ''
                              ? null
                              : event.target.value,
                        },
                        index
                      )
                    }
                  >
                    {maxMonthOptions.map(option => (
                      <MenuItem
                        key={option.value ?? 'none'}
                        value={option.value ?? ''}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FieldWrapper>
            </Box>

            <Button
              variant="contained"
              style={{ backgroundColor: themeColors.error }}
              onClick={() => removeOverride(index)}
            >
              Delete Override
            </Button>
          </RecordContainer>
        );
      })}
    </RecordContainer>
  );
};
